define(function (require, exports, module) {
    // dependency level imports
    var MessageService = require('message-service/message-service');
    var logg = require('../../utils/core-log');
    var exists = require('extant/extant');

    // transitional util imports
    var GameId = require('gid-decoder/GidDecoder');

    var ModelCache = require('./ModelCache');
    var PassiveApiRequestItem = require('../monitor/PassiveApiRequestItem');
    var EventTypes = require('../../enums/DataEvents');
    var ApiHeartbeat = require('../monitor/ApiHeartbeatItem');
    var Config = require('../../utils/HeartbeatConfig');
    var promiseMany = require('promise-many/promise-many');
    var SocketHeartbeatItem = require('../monitor/SocketApiHeartbeatItem');

    var modeToHBItemMap = {
        'gumbo': ApiHeartbeat,
        'heartbeat': ApiHeartbeat,
        'passive': PassiveApiRequestItem,
        'passiveGumbo': PassiveApiRequestItem,
        'socket': SocketHeartbeatItem
    };

    /**
     * Class GameItem
     * @param gameID {String}
     * @returns {Object}
     *
     * The Manager object of an individual game. It contains some core details about the game (via the GID object)
     * and provides a pathway to the gameData / heartbeat etc associated with that game
     */
    function GameItem (gameID) {
        var that = this;
        var gid = GameId(gameID);
        this.gid = gid;
        this.gameID = gameID;
        this.gameIDStore = gid.storeVal;
        this.modelCache = new ModelCache(gid.storeVal);

        this.storeAndPublishResponseCB = function (responses) {
            return that.storeAndPublishResponse(responses);
        };

        MessageService.one(EventTypes.INIT_DATA_LOADED, function () {
            that.initialized = true;
            logg.log('dispatching game ready');
            MessageService.publish(EventTypes.GAME_INITIALIZED);
        });
    }

    // mixin public references to internal functions
    GameItem.prototype = {
        initialized: false,
        isPassive: false,
        hbMode: 'heartbeat',
        /**
         * Function init
         *
         * Activates the game item.
         */
        init: function (listenFunc, dispatchFunc) {
            if (!(this.initialized && exists(this.heartbeatItem))) {
                this.setMode();
            }
            this.heartbeatItem.init();
        },
        setMode: function () {
            this.config = Config;
            var mode;
            var isPassive = false;
            if (this.config.passiveMode === true) {
                mode = 'passiveGumbo';
                isPassive = true;
            } else if (this.config.socketMode === true) {
                mode = 'socket';
                this.webSocketWindow = this.config?.initProps?.webSocketWindow || 18;
                this.disableWebSockets = this.config?.initProps?.disableWebSockets;
                this.useNoDelayConnection = this.config?.initProps?.noDelayWebSockets === true;
            } 
            else {
                mode = 'gumbo';
            }
            this.hbMode = mode;
            this.isPassive = isPassive;
            this.heartbeatItem = new (modeToHBItemMap[mode] || modeToHBItemMap.heartbeat)(this.gameID, this);
        },
        /**
         * Function getGameData
         * @param flavor (*} the data you want as string, comma-delineated string, array or object
         * @returns {Object} a game data object with each requested datasource as an attribute on the root obj
         *
         * retrieves the game data for this game (as-is) from the model cache.
         * calling with no params returns the whole shebang
         */
        getGameData: function (flavor) {
            return this.modelCache.retrieveGameData(flavor).data;
        },
        /**
         * Function expireGameData
         * @param flavor (*} the data you want to expire as string, comma-delineated string, array or object
         *
         * Expires data from the ModelCache's backing store to flag it for a refresh
         */
        expireGameData: function (flavor) {
            this.modelCache.expireDatasources(flavor);
        },
        /**
         * Function requestGameData
         * @param flavor {*} the data you want as string, comma-delineated string, array or object
         * @param upID {String} the event id the for the results of this request to transport on
         * @param params {Object} optional additional name-value pairs for the datasource Manager
         *
         * this tells the model cache to check if the data is either loaded or fresh enough
         * it will Execute any necessary loads, and dispatch the results over the unique update id passed
         */
        requestGameData: function (datasources, params) {
            return this.getFreshGameData(datasources, params, false);
        },

        destroyGame: function () {
            this.modelCache = null;
            this.modelCache = new ModelCache(this.gameIDStore);
        },

        getFreshGameData: function (datasources, params, refresh) {
            if (!exists(datasources)) {
                datasources = [];
            }
            var datasourceList = this.modelCache.getDatasourceList(datasources);
            var requests = [];
            var i = 0;
            var len = datasourceList.length;
            var heartbeatItem = this.heartbeatItem;
            refresh = refresh === true;

            for (; i < len; i++) {
                // let the data-fetcher decide where to get info based on it's datasource definition
                requests.push(heartbeatItem.fetchAndStore(datasourceList[i], refresh, params));
            }

            if (requests.length) {
                var cb = this.storeAndPublishResponseCB;
                return promiseMany(requests).then(cb, cb);
            }
            return Promise.reject(null);
        },
        storeAndPublishResponse: function (responses) {
            return this.modelCache.retrieveGameData().data;
        },
        /**
         * Function getGameParams
         * @param overrides {Object} mix-ins for additional featurs
         * @returns {Object} default params + tfs host / time code (when applicable) passed mix-ins
         *
         * This provides an object of name-value pairs for most items going through datasource Manager
         */
        addGameParams: function (overrides) {
            return this.heartbeatItem.addGameParams(overrides);
        },
        pauseHeartbeat: function () {
            this.heartbeatItem.pause();
        },
        stopHeartbeat: function () {
            this.heartbeatItem.stop();
        },
        unStopHeartbeat: function () {
            this.heartbeatItem.start();
        },
        startHeartbeat: function () {
            this.heartbeatItem.start();
        },
        executePassiveLoad: function () {
            this.heartbeatItem.executeHeartbeat();
        },
        active: function () {
            return this.heartbeatItem.isActive();
        },
        updateConfig: function (updatedConfig = {}) {
            this.config.update(updatedConfig);
            /*
                Cases:
                    Socket is open and we get the call to close it
                    Socket is closed and we get the call to open it
            */
            if (this.hbMode === 'socket' && this.config?.initProps?.disableWebSockets) {
                // close the socket and fall back to the HB
                this.heartbeatItem.clean();
            } else if (this.hbMode !== 'socket' && this.config?.initProps?.disableWebSockets == false && this.config.socketMode == true) {
                // stop current heartbeat
                this.stopHeartbeat();
                //start the socket
                this.setMode();
                this.heartbeatItem.init();
            }  
        }
    };

    return GameItem;
});

